import type { GetServerSideProps, InferGetServerSidePropsType } from "next";
import { CategoryCard } from "@/components/category-card";
import { ContentPageIntro } from "@/components/content-page-intro";
import { MenuPageCanvas } from "@/components/menu-page-canvas";
import { PageSeoHead } from "@/components/page-seo-head";
import { getFooter, getMenuPage, getNavigation } from "@/lib/api";
import {
  buildBreadcrumbList,
  buildCategoryLinkList,
  buildMenuSchema,
} from "@/lib/seo-schema";
import type {
  StorefrontLayoutVariant,
  StorefrontShellData,
} from "@/lib/storefront-shell-types";
import {
  getStorefrontCanonicalOrigin,
  getStorefrontSiteConfig,
  resolveStorefrontSiteCode,
  type StorefrontSiteCode,
} from "@/lib/storefront-site";
import type {
  CategorySummary,
  ContentBlock,
  NavigationItem,
  SeoData,
} from "@/lib/storefront-types";

type MenuPageProps = {
  categories: CategorySummary[];
  blocks: ContentBlock[];
  navigationItems: NavigationItem[];
  shellData: StorefrontShellData;
  layoutVariant: StorefrontLayoutVariant;
  siteCode: StorefrontSiteCode;
  title: string;
  seo: SeoData;
};

export const getServerSideProps: GetServerSideProps<MenuPageProps> = async (
  context,
) => {
  const siteCode = resolveStorefrontSiteCode({
    configuredSiteCode: process.env.STOREFRONT_SITE,
    hostname: context.req.headers.host,
  });
  const [navigation, menuPage, footer] = await Promise.all([
    getNavigation(siteCode),
    getMenuPage(siteCode),
    getFooter(siteCode),
  ]);
  const menuTitle = menuPage.title || "Menu";
  const menuSeo = menuPage.seo ?? {
    pageTitle: menuTitle,
    seoTitle: menuTitle,
    seoDescription: "",
    seoImage: null,
    canonicalUrl: "",
    noIndex: false,
    noFollow: false,
  };

  return {
    props: {
      categories: menuPage.categories,
      blocks: menuPage.blocks,
      navigationItems: navigation.items,
      shellData: {
        footer,
        menuCategories: menuPage.categories,
        currentPageTitle: menuTitle,
        loginRegisterInsertAfter: navigation.loginRegisterInsertAfter,
      },
      layoutVariant: "immersive",
      siteCode,
      title: menuTitle,
      seo: menuSeo,
    },
  };
};

export default function MenuPage({
  categories,
  blocks,
  seo,
  siteCode,
  title,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const origin = getStorefrontCanonicalOrigin(siteCode);
  const structuredData: object[] = [
    buildBreadcrumbList(origin, [
      { name: "Home", path: "/" },
      { name: title, path: "/menu" },
    ]),
  ];
  // Menu/MenuSection is restaurant vocabulary — the Frozen site is a shop, so
  // its menu hub lists the categories as a plain ItemList instead.
  if (siteCode === "asorockfood") {
    structuredData.push(
      buildMenuSchema(
        origin,
        getStorefrontSiteConfig(siteCode).brandName,
        categories,
      ),
    );
  } else {
    structuredData.push(buildCategoryLinkList(origin, title, categories));
  }

  return (
    <>
      <PageSeoHead siteCode={siteCode} seo={seo} structuredData={structuredData} />
      <MenuPageCanvas
        blocks={blocks}
        topSection={
          <ContentPageIntro
            title={title}
            description={seo.seoDescription || null}
            showFallbackCard={false}
          />
        }
      >
          {categories.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-6">
              {categories.map((category) => (
                <CategoryCard key={category.slug} category={category} />
              ))}
            </div>
          ) : (
            <section className="storefront-page-panel rounded-2xl p-8">
              <h2 className="text-xl font-semibold storefront-text-heading">
                No categories available yet
              </h2>
              <p className="mt-3 max-w-2xl storefront-text-body">
                The menu categories will appear here as soon as they are
                available from Umbraco.
              </p>
            </section>
          )}
      </MenuPageCanvas>
    </>
  );
}
